import { AnalyticsStore, ContextStore } from 'bernie-plugin-mobx';
import { inject } from 'mobx-react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as React from 'react';

import { ErrorBoundary, Layer } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { QueryRoute, updateSearch } from 'bernie-core';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';

import { MultiItemStore } from 'stores/multi-item-store';

import { ESSAdapter, TypeaheadRenderType, UitkTypeahead } from 'uitk-react-typeahead';

import { EssClientLogger } from 'typeahead-client-logger';
import { FormFieldBinding } from 'components/common/search-form/fields/form-field-binding';
import { SearchFormFieldProps } from 'components/common/search-form';

export interface LocationProps extends LocalizedComponentProps, SearchFormFieldProps, RouteComponentProps {
  analytics?: AnalyticsStore;
  context?: ContextStore;
  essAnalyticsLogger?: EssClientLogger;
  multiItem?: MultiItemStore;
  maxTypeaheadResults?: number;
}

@withRouter
@withLocalization
@FormFieldBinding
@inject('analytics', 'context', 'multiItem')
export class Origin extends React.Component<LocationProps> {
  private pristine = true;
  private fieldRef;

  constructor(props) {
    super(props);

    this.dismissTypeahead = this.dismissTypeahead.bind(this);
    this.openTypeahead = this.openTypeahead.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.validate = this.validate.bind(this);
    this.formValidator = this.formValidator.bind(this);
    this.renderTypeaheadDialog = this.renderTypeaheadDialog.bind(this);
    this.fieldRef = React.createRef();
    props.registerValidator(this.formValidator);
  }

  // tslint:disable no-multiple-render-methods
  public renderTypeaheadDialog(children) {
    return (
      <QueryRoute query={{ pwaDialog: 'typeahead' }}>
        <Layer id="home-typeahead" onDismiss={this.dismissTypeahead}>
          {children}
        </Layer>
      </QueryRoute>
    );
  }

  public dismissTypeahead(renderType): void {
    const { analytics, history } = this.props;
    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent('DismissOriginTypeahead', 'Origin Typeahead Modal Dismissed', null);
    }
    if (renderType === TypeaheadRenderType.DIALOG) {
      history.goBack();
    }
    if (this.fieldRef && this.fieldRef.current && typeof this.fieldRef.current.focusTypeaheadInput === 'function') {
      this.fieldRef.current.focusTypeaheadInput();
    }
  }

  public openTypeahead(renderType): void {
    const { analytics, history, location } = this.props;

    if (renderType === TypeaheadRenderType.DIALOG) {
      updateSearch({ history, location, newParams: { pwaDialog: 'typeahead' } });
    }

    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent(':ChangeOrigin', 'User searching Origin', {});
    }
  }

  public handleUpdate(): (selection) => void {
    return selection => {
      const { updateModel } = this.props;

      const { data, term } = selection;
      const userHistory = data && data.category === 'USERHISTORY';

      if (typeof updateModel === 'function') {
        this.pristine = false;
        updateModel({
          target: {
            name: 'origin',
            value: term,
            track: true,
            userHistory,
          },
        });
      }
    };
  }

  private formValidator(): { focus: any; text } {
    this.pristine = false;

    return this.validate();
  }

  private validate(): { focus: any; text } {
    const { l10n, model } = this.props;
    const { formatText } = l10n;

    if (this.pristine) {
      return;
    }

    const origin = model && model.origin;
    const focus =
      this.fieldRef &&
      this.fieldRef.current &&
      typeof this.fieldRef.current.focusTypeaheadInput === 'function' &&
      this.fieldRef.current.focusTypeaheadInput;

    if (!origin) {
      return {
        text: formatText('hotels.searchWizard.multiitem.validation.invalidOrigin'),
        focus,
      };
    }
  }

  static defaultProps: Partial<LocationProps> = {
    maxTypeaheadResults: 10,
  };

  public render() {
    const { context, l10n, model, wrapperClassNames, multiItem, maxTypeaheadResults } = this.props;
    const { formatText } = l10n;

    const multiItemEssAdapterConfig = {
      dest: false,
      essLogger: this.props.essAnalyticsLogger,
      expuserid: context && context.user && context.user.expUserId,
      guid: context && context.deviceId,
      inputId: 'hotels-origin',
      locale: context && context.locale,
      maxResults: maxTypeaheadResults,
      siteId: context && context.site && context.site.id,
      client: 'Packages.Search',
      features: 'nearby_airport|ta_hierarchy|consistent_display',
      lob: 'PACKAGES',
      subLob: multiItem.getPackageType(),
      personalize: false,
      regionType: 863,
      packageType: multiItem.getPackageType(),
    };

    const defaultTypeaheadProps = {
      allowExactMatch: true,
      label: formatText('hotels.searchWizard.multiitem.origin'),
      icon: 'place',
      invalid: this.validate() && this.validate().text,
      name: 'origin',
      onClick: this.openTypeahead,
      onDismiss: this.dismissTypeahead,
      renderDialog: this.renderTypeaheadDialog,
      renderType: TypeaheadRenderType.ADAPTIVE,
      stid: 'trip-origin',
      value: model && model.origin,
      ref: this.fieldRef,
      onUpdate: this.handleUpdate(),
    };

    return (
      <ErrorBoundary label="Origin.typeahead">
        <div className={wrapperClassNames || 'uitk-cell all-y-gutter-two s-cell-1-1 m-cell-1-2 l-cell-1-3 xl-cell-1-3 location'}>
          <Experiment name="Remove_POI_and_Neighborhood_from_flight_and_package_typeahead">
            <ExperimentControl>
              <UitkTypeahead {...defaultTypeaheadProps} adapter={new ESSAdapter({ ...multiItemEssAdapterConfig })} />
            </ExperimentControl>
            <ExperimentVariant bucket={1}>
              <UitkTypeahead {...defaultTypeaheadProps} adapter={new ESSAdapter({ ...multiItemEssAdapterConfig, regionType: 839 })} />
            </ExperimentVariant>
          </Experiment>
        </div>
      </ErrorBoundary>
    );
  }
}
